import {
  addUser,
  editUser,
  getAllRoles,
  getAllUsers,
  getUserById,
  deleteUser,
  clearGetAllUsers,
  clearGetUserById,
  getAllRolesWithPermissions,
  getAllPermissions,
  addRoleWithPermissions,
  updateRoleWithPermissions,
  getRoleByIdWithPermissions,
  clearGetAllRolesWithPermissions,
  clearGetRoleByIdWithPermissions,
} from "./UsersActions";
import { createSlice } from "@reduxjs/toolkit";

interface UsersState {
  users: any[] | null;
  usersLoading: boolean;
  usersError: any | null;

  addUser: any | null;
  addUserLoading: boolean;
  addUserError: any | null;

  roles: { id: number; roleName: string }[] | null;
  rolesLoading: boolean;
  rolesError: any | null;

  rolesWithPermissions: any[] | null;
  rolesWithPermissionsLoading: boolean;
  rolesWithPermissionsError: any | null;

  userById: any | null;
  userByIdLoading: boolean;
  userByIdError: any | null;

  editUser: any | null;
  editUserLoading: boolean;
  editUserError: any | null;

  deleteUser: any | null;
  deleteUserLoading: boolean;
  deleteUserError: any | null;

  permissions: any[] | null;
  permissionsLoading: boolean;
  permissionsError: any | null;

  addRoleWithPermissionsData: any | null;
  addRoleWithPermissionsLoading: boolean;
  addRoleWithPermissionsError: any | null;

  updateRoleWithPermissionsData: any | null;
  updateRoleWithPermissionsLoading: boolean;
  updateRoleWithPermissionsError: any | null;

  roleByIdWithPermissionsData: any | null;
  roleByIdWithPermissionsLoading: boolean;
  roleByIdWithPermissionsError: any | null;
}

const initialState: UsersState = {
  users: null,
  usersLoading: false,
  usersError: null,

  addUser: null,
  addUserLoading: false,
  addUserError: null,

  roles: null,
  rolesLoading: false,
  rolesError: null,

  rolesWithPermissions: null,
  rolesWithPermissionsLoading: false,
  rolesWithPermissionsError: null,

  userById: null,
  userByIdLoading: false,
  userByIdError: null,

  editUser: null,
  editUserLoading: false,
  editUserError: null,

  deleteUser: null,
  deleteUserLoading: false,
  deleteUserError: null,

  permissions: null,
  permissionsLoading: false,
  permissionsError: null,

  addRoleWithPermissionsData: null,
  addRoleWithPermissionsLoading: false,
  addRoleWithPermissionsError: null,

  roleByIdWithPermissionsData: null,
  roleByIdWithPermissionsLoading: false,
  roleByIdWithPermissionsError: null,

  updateRoleWithPermissionsData: null,
  updateRoleWithPermissionsLoading: false,
  updateRoleWithPermissionsError: null,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllUsers.pending, (state) => {
      state.usersLoading = true;
      state.usersError = null;
      state.users = null;
    });

    builder.addCase(getAllUsers.fulfilled, (state, action) => {
      state.usersLoading = false;
      state.users = action.payload;
      state.usersError = null;
    });

    builder.addCase(getAllUsers.rejected, (state, action) => {
      state.usersLoading = false;
      state.usersError = action.payload;
      state.users = null;
    });

    builder.addCase(addUser.pending, (state) => {
      state.addUserLoading = true;
      state.addUserError = null;
      state.addUser = null;
    });

    builder.addCase(addUser.fulfilled, (state, action) => {
      state.addUserLoading = false;
      state.addUser = action.payload;
      state.addUserError = null;
    });

    builder.addCase(addUser.rejected, (state, action) => {
      state.addUserLoading = false;
      state.addUserError = action.payload;
      state.addUser = null;
    });

    builder.addCase(getAllRoles.pending, (state) => {
      state.rolesLoading = true;
      state.rolesError = null;
      state.roles = null;
    });

    builder.addCase(getAllRoles.fulfilled, (state, action) => {
      state.rolesLoading = false;
      state.roles = action.payload;
      state.rolesError = null;
    });

    builder.addCase(getAllRoles.rejected, (state, action) => {
      state.rolesLoading = false;
      state.rolesError = action.payload;
      state.roles = null;
    });

    builder.addCase(getAllRolesWithPermissions.pending, (state) => {
      state.rolesWithPermissionsLoading = true;
      state.rolesWithPermissionsError = null;
      state.rolesWithPermissions = null;
    });

    builder.addCase(getAllRolesWithPermissions.fulfilled, (state, action) => {
      state.rolesWithPermissionsLoading = false;
      state.rolesWithPermissions = action.payload;
      state.rolesWithPermissionsError = null;
    });

    builder.addCase(getAllRolesWithPermissions.rejected, (state, action) => {
      state.rolesWithPermissionsLoading = false;
      state.rolesWithPermissionsError = action.payload;
      state.rolesWithPermissions = null;
    });

    builder.addCase(getUserById.pending, (state) => {
      state.userByIdLoading = true;
      state.userByIdError = null;
      state.userById = null;
    });

    builder.addCase(getUserById.fulfilled, (state, action) => {
      state.userByIdLoading = false;
      state.userById = action.payload;
      state.userByIdError = null;
    });

    builder.addCase(getUserById.rejected, (state, action) => {
      state.userByIdLoading = false;
      state.userByIdError = action.payload;
      state.userById = null;
    });

    builder.addCase(editUser.pending, (state) => {
      state.editUserLoading = true;
      state.editUserError = null;
      state.editUser = null;
    });

    builder.addCase(editUser.fulfilled, (state, action) => {
      state.editUserLoading = false;
      state.editUser = action.payload;
      state.editUserError = null;
    });

    builder.addCase(editUser.rejected, (state, action) => {
      state.editUserLoading = false;
      state.editUserError = action.payload;
      state.editUser = null;
    });

    builder.addCase(deleteUser.pending, (state) => {
      state.deleteUserLoading = true;
      state.deleteUserError = null;
      state.deleteUser = null;
    });

    builder.addCase(deleteUser.fulfilled, (state, action) => {
      state.deleteUserLoading = false;
      state.deleteUser = action.payload;
      state.deleteUserError = null;
    });

    builder.addCase(deleteUser.rejected, (state, action) => {
      state.deleteUserLoading = false;
      state.deleteUserError = action.payload;
      state.deleteUser = null;
    });

    builder.addCase(clearGetAllUsers.fulfilled, (state) => {
      state.usersLoading = false;
      state.usersError = null;
      state.users = null;
    });

    builder.addCase(clearGetUserById.fulfilled, (state) => {
      state.userByIdLoading = false;
      state.userByIdError = null;
      state.userById = null;
    });

    builder.addCase(getAllPermissions.pending, (state) => {
      state.permissionsLoading = true;
      state.permissionsError = null;
      state.permissions = null;
    });

    builder.addCase(getAllPermissions.fulfilled, (state, action) => {
      state.permissionsLoading = false;
      state.permissions = action.payload;
      state.permissionsError = null;
    });

    builder.addCase(getAllPermissions.rejected, (state, action) => {
      state.permissionsLoading = false;
      state.permissionsError = action.payload;
      state.permissions = null;
    });

    builder.addCase(addRoleWithPermissions.pending, (state) => {
      state.addRoleWithPermissionsLoading = true;
      state.addRoleWithPermissionsError = null;
      state.addRoleWithPermissionsData = null;
    });

    builder.addCase(addRoleWithPermissions.fulfilled, (state, action) => {
      state.addRoleWithPermissionsLoading = false;
      state.addRoleWithPermissionsData = action.payload;
      state.addRoleWithPermissionsError = null;
    });

    builder.addCase(addRoleWithPermissions.rejected, (state, action) => {
      state.addRoleWithPermissionsLoading = false;
      state.addRoleWithPermissionsError = action.payload;
      state.addRoleWithPermissionsData = null;
    });

    builder.addCase(updateRoleWithPermissions.pending, (state) => {
      state.updateRoleWithPermissionsLoading = true;
      state.updateRoleWithPermissionsError = null;
      state.updateRoleWithPermissionsData = null;
    });

    builder.addCase(updateRoleWithPermissions.fulfilled, (state, action) => {
      state.updateRoleWithPermissionsLoading = false;
      state.updateRoleWithPermissionsData = action.payload;
      state.updateRoleWithPermissionsError = null;
    });

    builder.addCase(updateRoleWithPermissions.rejected, (state, action) => {
      state.updateRoleWithPermissionsLoading = false;
      state.updateRoleWithPermissionsError = action.payload;
      state.updateRoleWithPermissionsData = null;
    });

    builder.addCase(getRoleByIdWithPermissions.pending, (state) => {
      state.roleByIdWithPermissionsLoading = true;
      state.roleByIdWithPermissionsError = null;
      state.roleByIdWithPermissionsData = null;
    });

    builder.addCase(getRoleByIdWithPermissions.fulfilled, (state, action) => {
      state.roleByIdWithPermissionsLoading = false;
      state.roleByIdWithPermissionsData = action.payload;
      state.roleByIdWithPermissionsError = null;
    });

    builder.addCase(getRoleByIdWithPermissions.rejected, (state, action) => {
      state.roleByIdWithPermissionsLoading = false;
      state.roleByIdWithPermissionsError = action.payload;
      state.roleByIdWithPermissionsData = null;
    });

    builder.addCase(clearGetRoleByIdWithPermissions.fulfilled, (state) => {
      state.roleByIdWithPermissionsLoading = false;
      state.roleByIdWithPermissionsError = null;
      state.roleByIdWithPermissionsData = null;
    });

    builder.addCase(clearGetAllRolesWithPermissions.fulfilled, (state) => {
      state.rolesWithPermissionsLoading = false;
      state.rolesWithPermissionsError = null;
      state.rolesWithPermissions = null;
    });
  },
});

const usersReducer = usersSlice.reducer;

export default usersReducer;
